import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined'
import { api } from '../../api/client.js'
import { tokens } from '../../theme.js'
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  SecondaryButton,
  MedBodyText,
  MutedText,
  NativeSelect,
  RadioButton,
  KindBadge,
  ErrorText,
  TextField,
  plural
} from '../styles/index.js'
import { formatDay, TIER_LABEL } from '../common/text.js'
import { fileCodeCopy } from '../common/errorCopy.js'
import { displayKind } from '../files/fileStatus.js'

const colors = tokens.colors
const fontFamily = tokens.fonts.body

export const LEGISLATIONS = [
  { id: 'bc_fippa', label: 'BC FIPPA', helper: 'Freedom of Information and Protection of Privacy Act (BC).' },
  { id: 'sk_lafoip', label: 'SK LAFOIP', helper: 'Local Authority Freedom of Information and Protection of Privacy Act (SK).' }
]
const HINT = { NOT_CONVERTED: 'convert', EXCLUDED: 'restore' }
function refusalLine(refused, total) {
  const names = refused.map(f => f.name)
  const list = names.length > 3 ? `${names.slice(0, 3).join(', ')} and ${names.length - 3} more` : names.join(', ')
  const codes = new Set(refused.map(f => f.code))
  const verb = codes.size === 1 ? HINT[[...codes][0]] : null
  const hint = codes.has('COMBINED_SOURCE')
    ? `uncombine its combined document before preparing ${list}`
    : verb
      ? `${verb} ${list} first`
      : `remove ${list} from the selection`
  const who = refused.length === total ? (total === 1 ? 'This file' : 'These files') : `${refused.length} of ${total} files`
  return `${who} cannot be prepared: ${hint}.`
}
const localCode = f => (!f.pdfObjectKey ? 'NOT_CONVERTED' : f.status === 'PROCESSING' || f.status === 'CONVERTING' ? 'PENDING_OPERATION' : null)

const Label = styled.p`
  margin: 0 0 8px;
  font: 500 14px/1.4 ${fontFamily};
  color: ${colors.text.body};
`
const Files = styled.div`
  border: 1px solid ${colors.background.medium};
  border-radius: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: min(260px, 30vh);
  margin-bottom: 20px;
`
const Row = styled.div`
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) auto auto auto;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-bottom: 1px solid ${colors.background.medium};
  background: ${p => (p.$refused ? colors.errorLight : colors.white)};
  &:last-of-type {
    border-bottom: none;
  }
  & > span:nth-of-type(1) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${colors.primaryExtraLight};
    color: ${colors.primary};
    font: 600 11px/1 ${fontFamily};
  }
  & > span:nth-of-type(2) {
    min-width: 0;
    font: 400 14px/1.3 ${fontFamily};
    color: ${colors.text.body};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`
const Reason = styled.span`
  font: 400 12px/1.3 ${fontFamily};
  color: ${colors.error};
  white-space: nowrap;
`
const Two = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 20px;
`
const Option = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid ${p => (p.$on ? colors.primary : colors.background.medium)};
  background: ${p => (p.$on ? colors.primaryExtraLight : colors.white)};
  & + & {
    margin-top: 10px;
  }
  & > .MuiRadio-root {
    margin-top: 1px;
  }
`
const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 74px;
  resize: none;
  padding: 9px 12px;
  font: 400 14px/1.45 ${fontFamily};
  color: ${colors.inputField.text};
  background: ${colors.inputField.background};
  border: 1px solid ${colors.sectionDivider};
  border-radius: ${tokens.borderRadius.input};
  outline: none;
  &:focus {
    border-color: ${colors.inputField.focusedBorder};
  }
`
const Three = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
`

export function PrepareDialog({ open, projectId, files, containerLabel, onClose, onStarted }) {
  const [combine, setCombine] = useState(files.length > 1)
  const [legislation, setLegislation] = useState('bc_fippa')
  const [context, setContext] = useState('')
  const [name, setName] = useState('')
  const [org, setOrg] = useState('')
  const [email, setEmail] = useState('')
  const [refused, setRefused] = useState({}) // file id -> code, from the server
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!open) return
    setCombine(files.length > 1)
    setLegislation('bc_fippa')
    setContext('')
    setName('')
    setOrg('')
    setEmail('')
    setRefused({})
    setError('')
    setBusy(false)
  }, [open])

  const n = files.length
  const codeOf = f => refused[f.id] || localCode(f)
  const refusedFiles = files.filter(f => codeOf(f)).map(f => ({ name: f.name, code: codeOf(f) }))
  const blocked = refusedFiles.length
  const accepted = files.filter(f => !codeOf(f))
  const pages = accepted.reduce((t, f) => t + (f.pageCount || 0), 0)
  const summary = refusalLine(refusedFiles, n)
  const law = LEGISLATIONS.find(l => l.id === legislation)
  const requestorIdentity = [name.trim(), org.trim(), email.trim()].filter(Boolean).join(', ')

  async function start() {
    if (busy || blocked) return
    setBusy(true)
    setError('')
    try {
      const { data } = await api.post(`/api/projects/${projectId}/prepare`, {
        fileIds: files.map(f => f.id),
        combine,
        legislation,
        requestContext: context.trim(),
        requestorIdentity
      })
      onStarted(data, { fileIds: files.map(f => f.id), combine, legislation })
    } catch (e) {
      const body = e.response?.data
      if (body?.code === 'FILES_REJECTED') {
        setRefused(Object.fromEntries(body.rejected.map(r => [r.id, r.code])))
      } else if (body?.code === 'NOT_ENOUGH_FILES') {
        setError('Combining needs at least two files.')
      } else {
        setError(e.response ? "The preparation couldn't start." : 'No connection. Try again.')
      }
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      width={760}
      PaperProps={{ sx: { maxHeight: 'calc(100vh - 32px)' } }}
      data-testid="prepare-dialog">
      <DialogTitle style={{ marginBottom: 4 }}>Prepare for redaction</DialogTitle>
      <MutedText style={{ marginBottom: 18 }} data-testid="prepare-summary">
        {plural(accepted.length, 'file')} · {plural(pages, 'page')} · {containerLabel}
      </MutedText>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <Label>Selected files (in document order)</Label>
      </div>
      <Files>
        {files.map((f, i) => {
          const code = codeOf(f)
          return (
            <Row key={f.id} $refused={!!code} data-testid="prepare-file" data-file-name={f.name} data-file-id={f.id} data-code={code || undefined}>
              <span>{i + 1}</span>
              <span title={f.name}>{f.name}</span>
              {code ? <Reason data-testid="prepare-file-reason">{fileCodeCopy(code, 'Cannot be prepared')}</Reason> : <span />}
              <KindBadge
                kind={displayKind(f)}
                title={f.pdfObjectKey && f.kind !== 'PDF' && f.kind !== 'COMBINED' ? `Original format: ${f.kind}` : undefined}
              />
              <MutedText style={{ whiteSpace: 'nowrap' }}>
                {f.pageCount != null ? `${f.pageCount} p · ` : ''}
                {f.effectiveDate ? `${formatDay(f.effectiveDate)} · ${TIER_LABEL[f.effectiveDateSource] || 'uploaded'}` : ''}
              </MutedText>
            </Row>
          )
        })}
      </Files>

      <Two>
        <div>
          <Label>Output</Label>
          <Option $on={combine} style={n < 2 ? { opacity: 0.5, cursor: 'default' } : undefined}>
            <RadioButton
              checked={combine}
              onChange={() => setCombine(true)}
              disabled={busy || n < 2}
              inputProps={{ 'data-testid': 'prepare-combine' }}
            />
            <div>
              <MedBodyText>Combine into one document</MedBodyText>
              <MutedText style={{ fontSize: 12 }}>
                {n < 2
                  ? 'Needs at least two files.'
                  : `${plural(accepted.length, 'file')} → 1 PDF, ${plural(pages, 'page')}, in the order above. One set of results.`}
              </MutedText>
            </div>
          </Option>
          <Option $on={!combine}>
            <RadioButton checked={!combine} onChange={() => setCombine(false)} disabled={busy} inputProps={{ 'data-testid': 'prepare-separate' }} />
            <div>
              <MedBodyText>Keep files separate</MedBodyText>
              <MutedText style={{ fontSize: 12 }}>
                {plural(accepted.length, 'PDF')}, {plural(accepted.length, 'set')} of results (annotated, redacted, reasons).
              </MutedText>
            </div>
          </Option>
        </div>
        <div>
          <Label>Legislation</Label>
          <NativeSelect value={legislation} onChange={e => setLegislation(e.target.value)} disabled={busy} data-testid="legislation-select">
            {LEGISLATIONS.map(l => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </NativeSelect>
          <MutedText style={{ fontSize: 12, marginTop: 8 }}>{law.helper}</MutedText>
        </div>
      </Two>

      <Label>Redaction context</Label>
      <TextArea
        value={context}
        onChange={e => setContext(e.target.value)}
        disabled={busy}
        placeholder="What the request covers, including subject, parties, and period."
        data-testid="redaction-context"
        aria-label="Redaction context"
      />
      <div style={{ height: 18 }} />

      <Label>Recipient details (not treated as third-party personal information)</Label>
      <Three>
        <TextField
          fullWidth
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Name"
          disabled={busy}
          inputProps={{ 'data-testid': 'requestor-name', 'aria-label': 'Recipient name' }}
        />
        <TextField
          fullWidth
          value={org}
          onChange={e => setOrg(e.target.value)}
          placeholder="Organisation (optional)"
          disabled={busy}
          inputProps={{ 'data-testid': 'requestor-org', 'aria-label': 'Recipient organisation' }}
        />
        <TextField
          fullWidth
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Email (optional)"
          disabled={busy}
          inputProps={{ 'data-testid': 'requestor-email', 'aria-label': 'Recipient email' }}
        />
      </Three>
      <DialogActions style={{ alignItems: 'center', marginTop: 20 }}>
        {blocked || error ? (
          <ErrorText role="alert" style={{ margin: '0 auto 0 0', fontSize: 12 }} data-testid="prepare-error">
            {blocked ? summary : error}
          </ErrorText>
        ) : (
          <span style={{ marginRight: 'auto' }} />
        )}
        <SecondaryButton type="button" onClick={onClose} disabled={busy} data-testid="dialog-cancel">
          Cancel
        </SecondaryButton>
        <Button type="button" onClick={start} disabled={blocked > 0 || n === 0} loading={busy} data-testid="prepare-start">
          <PlayArrowOutlinedIcon /> Start preparation
        </Button>
      </DialogActions>
    </Dialog>
  )
}
